import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { User, Lock, Bell, Shield, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SettingsPage() {
  const { user, getAuthHeaders } = useAuth();
  const [profile, setProfile] = useState({
    full_name: user?.full_name || '',
    organization: user?.organization || '',
    country: user?.country || '',
    phone: user?.phone || ''
  });
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [notifications, setNotifications] = useState({ deal_updates: true, match_alerts: true, market_alerts: false, weekly_digest: true });
  const [savingNotifs, setSavingNotifs] = useState(false);

  useEffect(() => {
    fetchNotificationPrefs();
  }, []);

  const fetchNotificationPrefs = async () => {
    try {
      const res = await axios.get(`${API}/auth/me/preferences`, { headers: getAuthHeaders() });
      if (res.data?.notification_preferences) {
        setNotifications(prev => ({ ...prev, ...res.data.notification_preferences }));
      }
    } catch (error) {
      // silently fail, use defaults
    }
  };

  const saveNotifications = async (newNotifs) => {
    setNotifications(newNotifs);
    setSavingNotifs(true);
    try {
      const prefsRes = await axios.get(`${API}/auth/me/preferences`, { headers: getAuthHeaders() });
      const existingPrefs = prefsRes.data || {};
      await axios.patch(`${API}/auth/me/preferences`, { ...existingPrefs, notification_preferences: newNotifs }, { headers: getAuthHeaders() });
      toast.success('Notification preferences saved');
    } catch (error) {
      toast.error('Failed to save notification preferences');
    } finally {
      setSavingNotifs(false);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/auth/me`, profile, { headers: getAuthHeaders() });
      toast.success('Profile updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (passwords.new_password !== passwords.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwords.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwords.current_password,
        new_password: passwords.new_password
      }, { headers: getAuthHeaders() });
      toast.success('Password changed');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">Manage your account and preferences</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <User size={18} />
            <CardTitle className="text-base">Profile Information</CardTitle>
          </div>
          <CardDescription>Update your personal details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Email</Label>
            <Input value={user?.email || ''} disabled className="bg-muted" />
          </div>
          <div>
            <Label>Role</Label>
            <Input value={user?.role || ''} disabled className="bg-muted capitalize" />
          </div>
          <div>
            <Label>Full Name</Label>
            <Input value={profile.full_name} onChange={(e) => setProfile({...profile, full_name: e.target.value})} data-testid="input-full-name" />
          </div>
          <div>
            <Label>Organization</Label>
            <Input value={profile.organization} onChange={(e) => setProfile({...profile, organization: e.target.value})} data-testid="input-organization" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Country</Label>
              <Input value={profile.country} onChange={(e) => setProfile({...profile, country: e.target.value})} data-testid="input-country" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={profile.phone} onChange={(e) => setProfile({...profile, phone: e.target.value})} data-testid="input-phone" />
            </div>
          </div>
          <Button onClick={saveProfile} disabled={saving} data-testid="save-profile-btn">
            <Save size={16} className="mr-2" />{saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Lock size={18} />
            <CardTitle className="text-base">Change Password</CardTitle>
          </div>
          <CardDescription>Update your account password</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Current Password</Label>
            <Input type="password" value={passwords.current_password} onChange={(e) => setPasswords({...passwords, current_password: e.target.value})} data-testid="input-current-password" />
          </div>
          <div>
            <Label>New Password</Label>
            <Input type="password" value={passwords.new_password} onChange={(e) => setPasswords({...passwords, new_password: e.target.value})} data-testid="input-new-password" />
          </div>
          <div>
            <Label>Confirm New Password</Label>
            <Input type="password" value={passwords.confirm_password} onChange={(e) => setPasswords({...passwords, confirm_password: e.target.value})} data-testid="input-confirm-password" />
          </div>
          <Button onClick={changePassword} disabled={changingPassword || !passwords.current_password || !passwords.new_password} variant="outline" data-testid="change-password-btn">
            <Lock size={16} className="mr-2" />{changingPassword ? 'Changing...' : 'Change Password'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bell size={18} />
            <CardTitle className="text-base">Notification Preferences</CardTitle>
          </div>
          <CardDescription>Control what notifications you receive</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div><p className="text-sm font-medium">Deal Updates</p><p className="text-xs text-muted-foreground">Notifications when deals you follow are updated</p></div>
            <Switch checked={notifications.deal_updates} onCheckedChange={(v) => saveNotifications({...notifications, deal_updates: v})} data-testid="toggle-deal-updates" />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div><p className="text-sm font-medium">Match Alerts</p><p className="text-xs text-muted-foreground">When new investor matches are found for your deals</p></div>
            <Switch checked={notifications.match_alerts} onCheckedChange={(v) => saveNotifications({...notifications, match_alerts: v})} data-testid="toggle-match-alerts" />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div><p className="text-sm font-medium">Market Alerts</p><p className="text-xs text-muted-foreground">Price movements and market intelligence updates</p></div>
            <Switch checked={notifications.market_alerts} onCheckedChange={(v) => saveNotifications({...notifications, market_alerts: v})} data-testid="toggle-market-alerts" />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div><p className="text-sm font-medium">Weekly Digest</p><p className="text-xs text-muted-foreground">Summary of platform activity and opportunities</p></div>
            <Switch checked={notifications.weekly_digest} onCheckedChange={(v) => saveNotifications({...notifications, weekly_digest: v})} data-testid="toggle-weekly-digest" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield size={18} />
            <CardTitle className="text-base">Account Info</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">User ID</span><span className="font-mono text-xs">{user?.id}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Account Created</span><span>{user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Status</span><span className="text-green-600">Active</span></div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
